/* eslint-disable react-hooks/set-state-in-effect -- mount-time data hydration */
import { useCallback, useEffect, useState } from "react";
import {
  Plus,
  Trash2,
  ExternalLink,
  Eye,
  EyeOff,
  RotateCcw,
  Loader2,
  ChevronRight,
  Save,
  ArrowLeft,
  GripVertical,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@/components/ui/tabs";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  fetchAdminAppPages,
  createAdminAppPage,
  updateAdminAppPage,
  deleteAdminAppPage,
  resetAdminAppPages,
} from "@/lib/appPagesApi";
import { useAppPages } from "@/contexts/AppPagesContext";
import ImageUploader from "@/components/admin/ImageUploader";
import { ICON_OPTIONS } from "@/lib/icons";

const newId = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const sectionKeys = ["hero", "screenshots", "features", "highlights", "faq", "cta"];

const ListEditor = ({ items, onChange, factory, renderRow, addLabel, testId, emptyLabel }) => {
  const update = (id, patch) =>
    onChange(items.map((it) => (it.id === id ? { ...it, ...patch } : it)));
  const remove = (id) => onChange(items.filter((it) => it.id !== id));
  const add = () => onChange([...items, { id: newId(), ...factory() }]);

  return (
    <div data-testid={testId} className="space-y-3">
      {items.length === 0 ? (
        <p className="text-xs text-[hsl(var(--muted-foreground))] italic">
          {emptyLabel}
        </p>
      ) : (
        items.map((it, i) => (
          <div
            key={it.id}
            data-testid={`${testId}-row-${i}`}
            className="rounded-xl border border-[hsl(var(--border))] bg-white p-4 space-y-3"
          >
            {renderRow(it, (patch) => update(it.id, patch), i)}
            <div className="flex justify-end">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => remove(it.id)}
                data-testid={`${testId}-remove-${i}`}
                className="text-[hsl(var(--muted-foreground))] hover:text-[#D94A14] h-8"
              >
                <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Remove
              </Button>
            </div>
          </div>
        ))
      )}
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={add}
        data-testid={`${testId}-add`}
        className="h-9 rounded-xl"
      >
        <Plus className="h-3.5 w-3.5 mr-1.5" /> {addLabel}
      </Button>
    </div>
  );
};

const IconSelect = ({ value, onChange, testId }) => (
  <Select value={value || "Sparkles"} onValueChange={onChange}>
    <SelectTrigger data-testid={testId} className="h-9">
      <SelectValue placeholder="Icon" />
    </SelectTrigger>
    <SelectContent className="max-h-72">
      {ICON_OPTIONS.map((name) => (
        <SelectItem key={name} value={name}>
          {name}
        </SelectItem>
      ))}
    </SelectContent>
  </Select>
);

const AppPageDetail = ({ initial, onSaved, onBack, onDelete }) => {
  const [draft, setDraft] = useState(initial);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setDraft(initial);
  }, [initial]);

  const onField = (k) => (e) =>
    setDraft({ ...draft, [k]: e.target.value });
  const onCheck = (k) => (v) => setDraft({ ...draft, [k]: v });

  const sections = draft.sections || {};
  const setSection = (k, v) =>
    setDraft({ ...draft, sections: { ...sections, [k]: v } });

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        slug: draft.slug,
        title: draft.title,
        tagline: draft.tagline,
        hero_eyebrow: draft.hero_eyebrow,
        hero_headline_line1: draft.hero_headline_line1,
        hero_headline_line2: draft.hero_headline_line2,
        hero_subheadline: draft.hero_subheadline,
        hero_image_id: draft.hero_image_id || null,
        coming_soon: !!draft.coming_soon,
        app_store_url: draft.app_store_url || "",
        play_store_url: draft.play_store_url || "",
        learn_more_url: draft.learn_more_url || "",
        accent_color: draft.accent_color || "#F35A1F",
        screenshots: draft.screenshots || [],
        screenshots_eyebrow: draft.screenshots_eyebrow,
        screenshots_title: draft.screenshots_title,
        features: draft.features || [],
        features_eyebrow: draft.features_eyebrow,
        features_title: draft.features_title,
        highlights: draft.highlights || [],
        highlights_eyebrow: draft.highlights_eyebrow,
        highlights_title: draft.highlights_title,
        faq: draft.faq || [],
        faq_eyebrow: draft.faq_eyebrow,
        faq_title: draft.faq_title,
        cta_eyebrow: draft.cta_eyebrow,
        cta_title: draft.cta_title,
        cta_subtitle: draft.cta_subtitle,
        cta_button_label: draft.cta_button_label,
        sections: { ...sections },
        visible: !!draft.visible,
        sort_order:
          typeof draft.sort_order === "number" ? draft.sort_order : 0,
      };
      const updated = await updateAdminAppPage(draft.id, payload);
      onSaved(updated);
      toast.success(`Saved "${updated.title}"`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save app page");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid={`app-page-editor-${draft.id}`}>
      {/* Top bar */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onBack}
            data-testid="app-page-editor-back"
            className="h-8"
          >
            <ArrowLeft className="h-3.5 w-3.5 mr-1.5" /> All pages
          </Button>
          <div className="hidden sm:flex items-center gap-2 text-xs text-[hsl(var(--muted-foreground))]">
            <span>{draft.visible ? "Public" : "Hidden"}</span>
            <span>·</span>
            <a
              href={`/apps/${draft.slug}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="app-page-editor-view-link"
              className="inline-flex items-center gap-1 hover:text-[#F35A1F]"
            >
              /apps/{draft.slug} <ExternalLink className="h-3 w-3" />
            </a>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                type="button"
                variant="outline"
                size="sm"
                data-testid="app-page-editor-delete"
                className="h-9 rounded-xl text-[#D94A14] border-[#FFD3BF] hover:bg-[#FFEEE6]"
              >
                <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Delete
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete {`"${draft.title}"`}?</AlertDialogTitle>
                <AlertDialogDescription>
                  This permanently removes the app page. Visitors going to
                  /apps/{draft.slug} will see a 404.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => onDelete(draft.id)}
                  data-testid="app-page-editor-delete-confirm"
                  className="bg-[#F35A1F] hover:bg-[#D94A14]"
                >
                  Delete page
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          <Button
            onClick={save}
            disabled={saving}
            data-testid="app-page-editor-save"
            className="h-9 rounded-xl bg-[#F35A1F] hover:bg-[#D94A14]"
          >
            {saving ? (
              <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
            ) : (
              <Save className="h-3.5 w-3.5 mr-1.5" />
            )}
            Save changes
          </Button>
        </div>
      </div>

      {/* Section visibility toggles */}
      <div
        data-testid="app-page-editor-sections-row"
        className="rounded-2xl border border-[hsl(var(--border))] bg-[#FFFAF6] p-4"
      >
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-[hsl(var(--muted-foreground))]">
              Page visibility
            </p>
            <div className="mt-1 flex items-center gap-3">
              <Switch
                checked={!!draft.visible}
                onCheckedChange={onCheck("visible")}
                data-testid="app-page-editor-visible-toggle"
              />
              <span className="text-sm">
                {draft.visible ? (
                  <span className="inline-flex items-center gap-1.5 text-[#1F9D6A] font-semibold">
                    <Eye className="h-3.5 w-3.5" /> Page is public
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 text-[hsl(var(--muted-foreground))]">
                    <EyeOff className="h-3.5 w-3.5" /> Page is hidden
                  </span>
                )}
              </span>
            </div>
          </div>
          <div className="flex-1 max-w-md">
            <p className="text-xs font-semibold uppercase tracking-wide text-[hsl(var(--muted-foreground))] mb-2">
              Sections shown on the page
            </p>
            <div className="grid grid-cols-3 gap-2">
              {sectionKeys.map((key) => (
                <label
                  key={key}
                  className="flex items-center justify-between gap-2 rounded-lg border border-[hsl(var(--border))] bg-white px-2 py-1.5"
                >
                  <span className="text-xs capitalize">{key}</span>
                  <Switch
                    checked={sections[key] !== false}
                    onCheckedChange={(v) => setSection(key, v)}
                    data-testid={`app-page-editor-section-${key}-toggle`}
                  />
                </label>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="basics" className="w-full">
        <TabsList
          data-testid="app-page-editor-tabs"
          className="flex flex-wrap h-auto gap-1 bg-[hsl(var(--secondary))] p-1 rounded-2xl"
        >
          {[
            ["basics", "Basics"],
            ["hero", "Hero"],
            ["screenshots", "Screenshots"],
            ["features", "Features"],
            ["highlights", "Highlights"],
            ["faq", "FAQ"],
            ["cta", "CTA"],
          ].map(([v, l]) => (
            <TabsTrigger
              key={v}
              value={v}
              data-testid={`app-page-editor-tab-${v}`}
              className="rounded-xl data-[state=active]:bg-white data-[state=active]:text-[#F35A1F]"
            >
              {l}
            </TabsTrigger>
          ))}
        </TabsList>

        {/* BASICS */}
        <TabsContent value="basics" className="m-0 mt-5 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="ap-title">App title</Label>
              <Input
                id="ap-title"
                data-testid="app-page-editor-title"
                value={draft.title || ""}
                onChange={onField("title")}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ap-slug">URL slug</Label>
              <Input
                id="ap-slug"
                data-testid="app-page-editor-slug"
                value={draft.slug || ""}
                onChange={onField("slug")}
              />
              <p className="text-[11px] text-[hsl(var(--muted-foreground))]">
                Visible at <span className="font-mono">/apps/{draft.slug}</span>
              </p>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ap-tagline">Tagline (short)</Label>
            <Input
              id="ap-tagline"
              data-testid="app-page-editor-tagline"
              value={draft.tagline || ""}
              onChange={onField("tagline")}
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="ap-accent">Accent colour (hex)</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="ap-accent"
                  data-testid="app-page-editor-accent"
                  value={draft.accent_color || "#F35A1F"}
                  onChange={onField("accent_color")}
                />
                <span
                  aria-hidden
                  className="inline-block h-9 w-9 rounded-lg border border-[hsl(var(--border))]"
                  style={{ backgroundColor: draft.accent_color || "#F35A1F" }}
                />
              </div>
            </div>
            <div className="space-y-1.5 flex items-end">
              <label className="flex items-center gap-3">
                <Switch
                  checked={!!draft.coming_soon}
                  onCheckedChange={onCheck("coming_soon")}
                  data-testid="app-page-editor-coming-soon"
                />
                <span className="text-sm">Mark as Coming Soon</span>
              </label>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="ap-appstore">App Store URL</Label>
              <Input
                id="ap-appstore"
                data-testid="app-page-editor-appstore"
                placeholder="https://apps.apple.com/..."
                value={draft.app_store_url || ""}
                onChange={onField("app_store_url")}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ap-play">Play Store URL</Label>
              <Input
                id="ap-play"
                data-testid="app-page-editor-playstore"
                placeholder="https://play.google.com/..."
                value={draft.play_store_url || ""}
                onChange={onField("play_store_url")}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ap-learn">Learn more URL</Label>
              <Input
                id="ap-learn"
                data-testid="app-page-editor-learnmore"
                placeholder="(optional)"
                value={draft.learn_more_url || ""}
                onChange={onField("learn_more_url")}
              />
            </div>
          </div>
        </TabsContent>

        {/* HERO */}
        <TabsContent value="hero" className="m-0 mt-5 space-y-5">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            <div className="lg:col-span-8 space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="ap-eyebrow">Eyebrow (small label above headline)</Label>
                <Input
                  id="ap-eyebrow"
                  data-testid="app-page-editor-hero-eyebrow"
                  value={draft.hero_eyebrow || ""}
                  onChange={onField("hero_eyebrow")}
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="ap-h1">Headline line 1</Label>
                  <Input
                    id="ap-h1"
                    data-testid="app-page-editor-hero-line1"
                    value={draft.hero_headline_line1 || ""}
                    onChange={onField("hero_headline_line1")}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="ap-h2">Headline line 2 (accent)</Label>
                  <Input
                    id="ap-h2"
                    data-testid="app-page-editor-hero-line2"
                    value={draft.hero_headline_line2 || ""}
                    onChange={onField("hero_headline_line2")}
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ap-sub">Subheadline</Label>
                <Textarea
                  id="ap-sub"
                  rows={4}
                  data-testid="app-page-editor-hero-sub"
                  value={draft.hero_subheadline || ""}
                  onChange={onField("hero_subheadline")}
                />
              </div>
            </div>
            <div className="lg:col-span-4">
              <ImageUploader
                value={draft.hero_image_id}
                onChange={(id) => setDraft({ ...draft, hero_image_id: id })}
                label="Hero phone screen image"
                testId="app-page-editor-hero-image"
                aspect="9/19.5"
              />
              <p className="mt-2 text-xs text-[hsl(var(--muted-foreground))]">
                Optional. Portrait app screenshot (~9:19.5) used inside the hero phone mockup.
              </p>
            </div>
          </div>
        </TabsContent>

        {/* SCREENSHOTS */}
        <TabsContent value="screenshots" className="m-0 mt-5 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Eyebrow</Label>
              <Input
                data-testid="app-page-editor-screenshots-eyebrow"
                value={draft.screenshots_eyebrow || ""}
                onChange={onField("screenshots_eyebrow")}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Section title</Label>
              <Input
                data-testid="app-page-editor-screenshots-title"
                value={draft.screenshots_title || ""}
                onChange={onField("screenshots_title")}
              />
            </div>
          </div>

          <ListEditor
            items={draft.screenshots || []}
            onChange={(arr) => setDraft({ ...draft, screenshots: arr })}
            factory={() => ({ image_id: null, caption: "" })}
            addLabel="Add screenshot"
            emptyLabel="No screenshots yet. Add up to 8 portrait images."
            testId="app-page-editor-screenshots"
            renderRow={(it, update, i) => (
              <div className="grid grid-cols-1 sm:grid-cols-12 gap-4 items-start">
                <div className="sm:col-span-1 flex sm:items-center sm:justify-center text-[hsl(var(--muted-foreground))]">
                  <GripVertical className="h-4 w-4" />
                  <span className="ml-1 text-xs">{i + 1}</span>
                </div>
                <div className="sm:col-span-5">
                  <ImageUploader
                    value={it.image_id}
                    onChange={(id) => update({ image_id: id })}
                    label="Screenshot"
                    testId={`app-page-editor-screenshot-image-${i}`}
                    aspect="9/19.5"
                  />
                </div>
                <div className="sm:col-span-6 space-y-1.5">
                  <Label>Caption (optional)</Label>
                  <Input
                    data-testid={`app-page-editor-screenshot-caption-${i}`}
                    value={it.caption || ""}
                    onChange={(e) => update({ caption: e.target.value })}
                  />
                </div>
              </div>
            )}
          />
        </TabsContent>

        {/* FEATURES */}
        <TabsContent value="features" className="m-0 mt-5 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Eyebrow</Label>
              <Input
                data-testid="app-page-editor-features-eyebrow"
                value={draft.features_eyebrow || ""}
                onChange={onField("features_eyebrow")}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Section title</Label>
              <Input
                data-testid="app-page-editor-features-title"
                value={draft.features_title || ""}
                onChange={onField("features_title")}
              />
            </div>
          </div>
          <ListEditor
            items={draft.features || []}
            onChange={(arr) => setDraft({ ...draft, features: arr })}
            factory={() => ({ icon: "Sparkles", title: "New feature", desc: "" })}
            addLabel="Add feature"
            emptyLabel="No features added yet."
            testId="app-page-editor-features"
            renderRow={(it, update, i) => (
              <div className="grid grid-cols-1 sm:grid-cols-12 gap-3">
                <div className="sm:col-span-3 space-y-1.5">
                  <Label>Icon</Label>
                  <IconSelect
                    value={it.icon}
                    onChange={(v) => update({ icon: v })}
                    testId={`app-page-editor-feature-icon-${i}`}
                  />
                </div>
                <div className="sm:col-span-9 space-y-1.5">
                  <Label>Title</Label>
                  <Input
                    data-testid={`app-page-editor-feature-title-${i}`}
                    value={it.title || ""}
                    onChange={(e) => update({ title: e.target.value })}
                  />
                </div>
                <div className="sm:col-span-12 space-y-1.5">
                  <Label>Description</Label>
                  <Textarea
                    rows={2}
                    data-testid={`app-page-editor-feature-desc-${i}`}
                    value={it.desc || ""}
                    onChange={(e) => update({ desc: e.target.value })}
                  />
                </div>
              </div>
            )}
          />
        </TabsContent>

        {/* HIGHLIGHTS */}
        <TabsContent value="highlights" className="m-0 mt-5 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Eyebrow</Label>
              <Input
                data-testid="app-page-editor-highlights-eyebrow"
                value={draft.highlights_eyebrow || ""}
                onChange={onField("highlights_eyebrow")}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Section title</Label>
              <Input
                data-testid="app-page-editor-highlights-title"
                value={draft.highlights_title || ""}
                onChange={onField("highlights_title")}
              />
            </div>
          </div>
          <ListEditor
            items={draft.highlights || []}
            onChange={(arr) => setDraft({ ...draft, highlights: arr })}
            factory={() => ({ icon: "Sparkles", title: "Why it's special", desc: "" })}
            addLabel="Add highlight"
            emptyLabel="No highlights yet."
            testId="app-page-editor-highlights"
            renderRow={(it, update, i) => (
              <div className="grid grid-cols-1 sm:grid-cols-12 gap-3">
                <div className="sm:col-span-3 space-y-1.5">
                  <Label>Icon</Label>
                  <IconSelect
                    value={it.icon}
                    onChange={(v) => update({ icon: v })}
                    testId={`app-page-editor-highlight-icon-${i}`}
                  />
                </div>
                <div className="sm:col-span-9 space-y-1.5">
                  <Label>Title</Label>
                  <Input
                    data-testid={`app-page-editor-highlight-title-${i}`}
                    value={it.title || ""}
                    onChange={(e) => update({ title: e.target.value })}
                  />
                </div>
                <div className="sm:col-span-12 space-y-1.5">
                  <Label>Description</Label>
                  <Textarea
                    rows={2}
                    data-testid={`app-page-editor-highlight-desc-${i}`}
                    value={it.desc || ""}
                    onChange={(e) => update({ desc: e.target.value })}
                  />
                </div>
              </div>
            )}
          />
        </TabsContent>

        {/* FAQ */}
        <TabsContent value="faq" className="m-0 mt-5 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Eyebrow</Label>
              <Input
                data-testid="app-page-editor-faq-eyebrow"
                value={draft.faq_eyebrow || ""}
                onChange={onField("faq_eyebrow")}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Section title</Label>
              <Input
                data-testid="app-page-editor-faq-title"
                value={draft.faq_title || ""}
                onChange={onField("faq_title")}
              />
            </div>
          </div>
          <ListEditor
            items={draft.faq || []}
            onChange={(arr) => setDraft({ ...draft, faq: arr })}
            factory={() => ({ q: "New question?", a: "" })}
            addLabel="Add FAQ item"
            emptyLabel="No FAQ items yet."
            testId="app-page-editor-faq"
            renderRow={(it, update, i) => (
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <Label>Question</Label>
                  <Input
                    data-testid={`app-page-editor-faq-q-${i}`}
                    value={it.q || ""}
                    onChange={(e) => update({ q: e.target.value })}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Answer</Label>
                  <Textarea
                    rows={3}
                    data-testid={`app-page-editor-faq-a-${i}`}
                    value={it.a || ""}
                    onChange={(e) => update({ a: e.target.value })}
                  />
                </div>
              </div>
            )}
          />
        </TabsContent>

        {/* CTA */}
        <TabsContent value="cta" className="m-0 mt-5 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Eyebrow</Label>
              <Input
                data-testid="app-page-editor-cta-eyebrow"
                value={draft.cta_eyebrow || ""}
                onChange={onField("cta_eyebrow")}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Button label</Label>
              <Input
                data-testid="app-page-editor-cta-button"
                value={draft.cta_button_label || ""}
                onChange={onField("cta_button_label")}
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Title</Label>
            <Input
              data-testid="app-page-editor-cta-title"
              value={draft.cta_title || ""}
              onChange={onField("cta_title")}
            />
          </div>
          <div className="space-y-1.5">
            <Label>Subtitle</Label>
            <Textarea
              rows={2}
              data-testid="app-page-editor-cta-sub"
              value={draft.cta_subtitle || ""}
              onChange={onField("cta_subtitle")}
            />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export const AppPagesEditor = () => {
  const { refresh: refreshPublic } = useAppPages();
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [resetting, setResetting] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [newPage, setNewPage] = useState({ title: "", slug: "", tagline: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchAdminAppPages();
      setPages(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load app pages");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const selected = pages.find((p) => p.id === selectedId);

  const handleSaved = async (updated) => {
    setPages((prev) =>
      prev.map((p) => (p.id === updated.id ? updated : p))
    );
    refreshPublic();
  };

  const handleDelete = async (id) => {
    try {
      await deleteAdminAppPage(id);
      setPages((prev) => prev.filter((p) => p.id !== id));
      setSelectedId(null);
      refreshPublic();
      toast.success("App page deleted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete");
    }
  };

  const handleCreate = async () => {
    if (!newPage.title.trim()) {
      toast.error("Title is required");
      return;
    }
    setCreating(true);
    try {
      const created = await createAdminAppPage({
        title: newPage.title.trim(),
        slug: newPage.slug.trim() || undefined,
        tagline: newPage.tagline.trim() || "",
      });
      setPages((prev) => [...prev, created]);
      setSelectedId(created.id);
      setCreateOpen(false);
      setNewPage({ title: "", slug: "", tagline: "" });
      refreshPublic();
      toast.success(`Created "${created.title}"`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create page");
    } finally {
      setCreating(false);
    }
  };

  const handleReset = async () => {
    setResetting(true);
    try {
      const data = await resetAdminAppPages();
      setPages(Array.isArray(data) ? data : []);
      refreshPublic();
      toast.success("Default app pages restored");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to reset");
    } finally {
      setResetting(false);
    }
  };

  if (loading) {
    return (
      <div
        data-testid="app-pages-editor-loading"
        className="flex items-center justify-center py-20"
      >
        <Loader2 className="h-6 w-6 animate-spin text-[#F35A1F]" />
      </div>
    );
  }

  if (selected) {
    return (
      <AppPageDetail
        initial={selected}
        onSaved={handleSaved}
        onBack={() => setSelectedId(null)}
        onDelete={handleDelete}
      />
    );
  }

  return (
    <div className="space-y-6" data-testid="app-pages-editor">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="pf-display text-[20px]">App Pages</h2>
          <p className="text-sm text-[hsl(var(--muted-foreground))]">
            Build a dedicated, fully customisable page for each app at{" "}
            <span className="font-mono">/apps/&lt;slug&gt;</span>.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                data-testid="app-pages-editor-reseed"
                className="h-9 rounded-xl"
                disabled={resetting}
              >
                {resetting ? (
                  <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                ) : (
                  <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
                )}
                Restore defaults
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Restore default app pages?</AlertDialogTitle>
                <AlertDialogDescription>
                  This re-adds the original Clever Canine, Fridge Clear AI,
                  and Colletr pages if they&apos;re missing. Existing pages with
                  matching slugs are left untouched.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleReset}
                  data-testid="app-pages-editor-reseed-confirm"
                  className="bg-[#F35A1F] hover:bg-[#D94A14]"
                >
                  Restore
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button
                size="sm"
                data-testid="app-pages-editor-new-button"
                className="h-9 rounded-xl bg-[#F35A1F] hover:bg-[#D94A14]"
              >
                <Plus className="h-3.5 w-3.5 mr-1.5" /> New page
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create a new app page</DialogTitle>
                <DialogDescription>
                  Give it a title and an optional URL slug. You can edit
                  everything else after it&apos;s created.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="np-title">Title</Label>
                  <Input
                    id="np-title"
                    data-testid="app-pages-editor-new-title"
                    placeholder="My amazing app"
                    value={newPage.title}
                    onChange={(e) =>
                      setNewPage((p) => ({ ...p, title: e.target.value }))
                    }
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="np-slug">URL slug (optional)</Label>
                  <Input
                    id="np-slug"
                    data-testid="app-pages-editor-new-slug"
                    placeholder="my-amazing-app"
                    value={newPage.slug}
                    onChange={(e) =>
                      setNewPage((p) => ({ ...p, slug: e.target.value }))
                    }
                  />
                  <p className="text-[11px] text-[hsl(var(--muted-foreground))]">
                    Leave blank to auto-generate from the title.
                  </p>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="np-tagline">Tagline (optional)</Label>
                  <Input
                    id="np-tagline"
                    data-testid="app-pages-editor-new-tagline"
                    value={newPage.tagline}
                    onChange={(e) =>
                      setNewPage((p) => ({ ...p, tagline: e.target.value }))
                    }
                  />
                </div>
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setCreateOpen(false)}
                  className="rounded-xl"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleCreate}
                  disabled={creating}
                  data-testid="app-pages-editor-new-confirm"
                  className="rounded-xl bg-[#F35A1F] hover:bg-[#D94A14]"
                >
                  {creating ? (
                    <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                  ) : (
                    <Plus className="h-3.5 w-3.5 mr-1.5" />
                  )}
                  Create page
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {pages.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-[hsl(var(--border))] bg-white p-10 text-center">
          <p className="text-sm text-[hsl(var(--muted-foreground))]">
            No app pages yet. Create your first page above, or restore the
            three Play Fox defaults.
          </p>
        </div>
      ) : (
        <ul className="space-y-2" data-testid="app-pages-editor-list">
          {pages.map((p) => (
            <li
              key={p.id}
              data-testid={`app-pages-editor-row-${p.slug}`}
              className="rounded-2xl border border-[hsl(var(--border))] bg-white p-4 flex flex-wrap items-center justify-between gap-3"
            >
              <div className="flex items-center gap-3 min-w-0">
                <span
                  aria-hidden
                  className="inline-flex h-9 w-9 items-center justify-center rounded-xl text-white text-sm font-semibold"
                  style={{ backgroundColor: p.accent_color || "#F35A1F" }}
                >
                  {p.title?.charAt(0)?.toUpperCase() || "A"}
                </span>
                <div className="min-w-0">
                  <p className="pf-display text-[16px] truncate">{p.title}</p>
                  <p className="text-xs text-[hsl(var(--muted-foreground))] truncate font-mono">
                    /apps/{p.slug}
                    {p.tagline ? ` · ${p.tagline}` : ""}
                  </p>
                </div>
                {p.coming_soon && (
                  <Badge className="rounded-full bg-[#FFEEE6] text-[#D94A14] hover:bg-[#FFEEE6] border-0 px-2 py-0.5 text-[10px] font-semibold">
                    Coming Soon
                  </Badge>
                )}
                {!p.visible && (
                  <Badge className="rounded-full bg-[hsl(var(--secondary))] text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--secondary))] border-0 px-2 py-0.5 text-[10px] font-semibold">
                    Hidden
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-2">
                <a
                  href={`/apps/${p.slug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`app-pages-editor-row-${p.slug}-view`}
                  className="inline-flex items-center gap-1 rounded-lg border border-[hsl(var(--border))] bg-white px-2 py-1.5 text-xs hover:bg-[hsl(var(--accent))]"
                >
                  <ExternalLink className="h-3 w-3" /> View
                </a>
                <Button
                  type="button"
                  size="sm"
                  onClick={() => setSelectedId(p.id)}
                  data-testid={`app-pages-editor-row-${p.slug}-edit`}
                  className="h-8 rounded-xl bg-[#121316] hover:bg-[#1A1C20] text-white"
                >
                  Edit <ChevronRight className="h-3.5 w-3.5 ml-1" />
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default AppPagesEditor;
